import { useState, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Award, RotateCcw, Eye } from 'lucide-react';
import { useCourse } from '../context/CourseContext';
import { cn } from '../lib/utils';

export default function QuizSection({ sectionId, questions, sectionTitle }) {
  const { completedQuizzes, markQuizComplete } = useCourse();
  const existingResult = completedQuizzes[sectionId];

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(!!existingResult);
  const [score, setScore] = useState(existingResult?.score || 0);
  const [shakeId, setShakeId] = useState(null);

  const handleAnswer = useCallback((questionId, value) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, [submitted]);

  const handleSubmit = useCallback(() => {
    if (!questions) return;
    let correct = 0;
    questions.forEach(q => {
      const userAnswer = answers[q.id];
      if (q.type === 'truefalse') {
        if ((userAnswer === 'true') === q.correct) correct++;
      } else {
        if (parseInt(userAnswer) === q.correct) correct++;
      }
    });
    setScore(correct);
    setSubmitted(true);
    markQuizComplete(sectionId, correct, questions.length);
  }, [answers, questions, sectionId, markQuizComplete]);

  const handleRetry = useCallback(() => {
    setAnswers({});
    setSubmitted(false);
    setScore(0);
  }, []);

  const isCorrect = (question) => {
    const userAnswer = answers[question.id];
    if (!userAnswer) return null;
    if (question.type === 'truefalse') {
      return (userAnswer === 'true') === question.correct;
    }
    return parseInt(userAnswer) === question.correct;
  };

  if (!questions || questions.length === 0) {
    return <p className="text-muted-foreground">No quiz available for this section.</p>;
  }

  const allAnswered = questions.every(q => answers[q.id] !== undefined);
  const passed = score >= Math.ceil(questions.length / 2);

  return (
    <div data-testid={`quiz-section-${sectionId}`}>
      <div className="flex items-center gap-3 mb-6">
        <Award className="h-6 w-6 text-sf-blue" />
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-foreground">Section Quiz</h2>
          <p className="text-sm text-muted-foreground">{sectionTitle}</p>
        </div>
      </div>

      {submitted && (
        <div className={cn(
          "rounded-xl p-5 mb-6 border flex items-center justify-between",
          passed ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800" : "bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800"
        )} data-testid="quiz-result">
          <div className="flex items-center gap-3">
            {passed ? (
              <CheckCircle className="h-8 w-8 text-sf-success" />
            ) : (
              <XCircle className="h-8 w-8 text-sf-warning" />
            )}
            <div>
              <p className="font-heading font-semibold text-foreground">
                {passed ? 'Well done!' : 'Keep learning!'}
              </p>
              <p className="text-sm text-muted-foreground">
                You scored {score} out of {questions.length}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleRetry} data-testid="retry-quiz-btn">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Retry
          </Button>
        </div>
      )}

      {submitted && (
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-border" data-testid="quiz-review-header">
          <Eye className="h-4 w-4 text-primary" />
          <p className="text-sm font-medium text-foreground">Review Mode</p>
          <span className="text-xs text-muted-foreground">- Correct answers are highlighted in green</span>
        </div>
      )}

      <div className="space-y-6">
        {questions.map((q, qIdx) => {
          const correct = submitted ? isCorrect(q) : null;
          return (
            <Card
              key={q.id}
              className={cn(
                "border transition-all duration-200",
                submitted && correct === true && "border-sf-success/30",
                submitted && correct === false && "border-destructive/30",
                shakeId === q.id && "shake"
              )}
              data-testid={`quiz-question-${q.id}`}
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-sf-blue text-white text-xs font-semibold flex items-center justify-center">
                    {qIdx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-foreground leading-snug">{q.question}</p>
                    <span className="text-xs text-muted-foreground mt-1 inline-block uppercase tracking-wider">
                      {q.type === 'truefalse' ? 'True / False' : 'Multiple Choice'}
                    </span>
                  </div>
                </div>

                <RadioGroup
                  value={answers[q.id] || ''}
                  onValueChange={(val) => handleAnswer(q.id, val)}
                  className="space-y-2 ml-10"
                >
                  {q.type === 'truefalse' ? (
                    ['true', 'false'].map((val) => {
                      const isSelected = answers[q.id] === val;
                      const isCorrectAnswer = submitted && ((val === 'true') === q.correct);
                      const isWrongChoice = submitted && isSelected && !isCorrectAnswer;
                      return (
                        <div
                          key={val}
                          className={cn(
                            "quiz-option flex items-center space-x-3 p-3 rounded-lg border cursor-pointer",
                            !submitted && isSelected && "selected",
                            submitted && isCorrectAnswer && "correct",
                            submitted && isWrongChoice && "incorrect"
                          )}
                        >
                          <RadioGroupItem value={val} id={`${q.id}-${val}`} disabled={submitted} />
                          <Label htmlFor={`${q.id}-${val}`} className="cursor-pointer flex-1 text-sm">
                            {val === 'true' ? 'True' : 'False'}
                          </Label>
                          {submitted && isCorrectAnswer && <CheckCircle className="h-4 w-4 text-sf-success" />}
                          {submitted && isWrongChoice && <XCircle className="h-4 w-4 text-destructive" />}
                        </div>
                      );
                    })
                  ) : (
                    q.options.map((opt, optIdx) => {
                      const isSelected = answers[q.id] === String(optIdx);
                      const isCorrectAnswer = submitted && optIdx === q.correct;
                      const isWrongChoice = submitted && isSelected && optIdx !== q.correct;
                      return (
                        <div
                          key={optIdx}
                          className={cn(
                            "quiz-option flex items-center space-x-3 p-3 rounded-lg border cursor-pointer",
                            !submitted && isSelected && "selected",
                            submitted && isCorrectAnswer && "correct",
                            submitted && isWrongChoice && "incorrect"
                          )}
                        >
                          <RadioGroupItem value={String(optIdx)} id={`${q.id}-${optIdx}`} disabled={submitted} />
                          <Label htmlFor={`${q.id}-${optIdx}`} className="cursor-pointer flex-1 text-sm">
                            {opt}
                          </Label>
                          {submitted && isCorrectAnswer && <CheckCircle className="h-4 w-4 text-sf-success" />}
                          {submitted && isWrongChoice && <XCircle className="h-4 w-4 text-destructive" />}
                        </div>
                      );
                    })
                  )}
                </RadioGroup>

                {submitted && q.explanation && (
                  <div className="mt-3 ml-10 p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Explanation: </span>
                      {q.explanation}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {!submitted && (
        <div className="mt-8">
          <Button
            onClick={handleSubmit}
            disabled={!allAnswered}
            className="bg-sf-blue hover:bg-sf-blue-dark text-white"
            data-testid="submit-quiz-btn"
          >
            Submit Answers
          </Button>
          {!allAnswered && (
            <p className="text-xs text-muted-foreground mt-2">
              Answer all questions to submit
            </p>
          )}
        </div>
      )}
    </div>
  );
}
